package org.wordsapp.ui.screen

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.wordsapp.model.ReviewGrade
import org.wordsapp.theme.*
import org.wordsapp.ui.component.PrimaryButton
import org.wordsapp.viewmodel.WordViewModel

/**
 * Endless flashcard mode: shows the translation, the user recalls the foreign
 * word, reveals it, and self-grades with SM-2 grades. No rounds — a new card
 * is drawn (weighted by due-ness) after every grade.
 */
@Composable
fun ReviewScreen(viewModel: WordViewModel) {
    val word by viewModel.reviewWord.collectAsState()
    val revealed by viewModel.reviewRevealed.collectAsState()
    val reviewedCount by viewModel.reviewedCount.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.ensureReviewStarted()
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(
            text = "Reviewed this session: $reviewedCount",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.align(Alignment.End),
        )

        Spacer(Modifier.height(24.dp))

        val current = word
        when {
            isLoading -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
            current == null -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                Text(
                    text = "No words to review yet.\nAdd words to your spreadsheet first.",
                    textAlign = TextAlign.Center,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            else -> {
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(16.dp),
                    elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(24.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                    ) {
                        Text(
                            text = "Recall the German word:",
                            style = MaterialTheme.typography.labelLarge,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                        Spacer(Modifier.height(16.dp))
                        Text(
                            text = current.translation,
                            fontSize = 28.sp,
                            fontWeight = FontWeight.Bold,
                            textAlign = TextAlign.Center,
                        )
                        if (revealed) {
                            Spacer(Modifier.height(16.dp))
                            HorizontalDivider()
                            Spacer(Modifier.height(16.dp))
                            Text(
                                text = current.foreignWord,
                                fontSize = 28.sp,
                                fontWeight = FontWeight.Bold,
                                textAlign = TextAlign.Center,
                                color = MaterialTheme.colorScheme.primary,
                            )
                        }
                    }
                }

                Spacer(Modifier.height(24.dp))

                if (!revealed) {
                    PrimaryButton(
                        text = "Show word",
                        onClick = { viewModel.revealReviewAnswer() },
                    )
                } else {
                    Text(
                        text = "How hard was it to recall?",
                        style = MaterialTheme.typography.labelLarge,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                    Spacer(Modifier.height(12.dp))
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        GradeButton("Again", Red500, Modifier.weight(1f)) {
                            viewModel.gradeReviewWord(ReviewGrade.AGAIN)
                        }
                        GradeButton("Hard", Orange500, Modifier.weight(1f)) {
                            viewModel.gradeReviewWord(ReviewGrade.HARD)
                        }
                        GradeButton("Good", Green500, Modifier.weight(1f)) {
                            viewModel.gradeReviewWord(ReviewGrade.GOOD)
                        }
                        GradeButton("Easy", Blue500, Modifier.weight(1f)) {
                            viewModel.gradeReviewWord(ReviewGrade.EASY)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun GradeButton(
    label: String,
    color: Color,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    Button(
        onClick = onClick,
        modifier = modifier,
        shape = RoundedCornerShape(12.dp),
        colors = ButtonDefaults.buttonColors(containerColor = color),
        contentPadding = PaddingValues(horizontal = 4.dp, vertical = 12.dp),
    ) {
        Text(label, maxLines = 1)
    }
}
